// The source-issues queue as the screen reads it (E32-S8-T4; ADR-031): pure functions over the
// contract's SourceIssue, so the rules the mockup (mockups/issues-queue.html) draws — which form an
// issue opens on, what holds a render, what the filters keep — are one place and testable alone.
import {
  summarizeSourceIssues,
  type SourceIssue,
  type SourceIssueQueue,
  type SourceIssueStatus,
} from "@waypoint/contracts";

/** The three ways to answer, and the order the panel offers them. */
export type AnswerMode = "reading" | "text" | "confirm";

/** The status filter: the contract's states, plus "blocking" (open and holding a render). */
export type StatusFilter = SourceIssueStatus | "blocking";

export interface Filters {
  readonly kind: string;
  readonly origin: string;
  readonly guide: string;
  readonly status: StatusFilter | "";
}

export const NO_FILTERS: Filters = { kind: "", origin: "", guide: "", status: "" };

/** An open contradiction: the kind that gates. */
export function isBlocking(issue: SourceIssue): boolean {
  return issue.status === "open" && issue.kind === "contradiction";
}

/** An open contradiction some segment narrates: its renders are refused (ADR-031; summary.blocking). */
export function holdsRender(issue: SourceIssue): boolean {
  return isBlocking(issue) && issue.segments.length > 0;
}

/** Whether the issue was reopened with a follow-up and is open again. */
export function isReopened(issue: SourceIssue): boolean {
  return issue.status === "open" && issue.followUps.length > 0;
}

export function citedPages(issue: SourceIssue): readonly string[] {
  return issue.pages.map((p) => p.page);
}

export function provisionalPages(issue: SourceIssue): readonly string[] {
  return issue.pages.filter((p) => p.provisional === true).map((p) => p.page);
}

/** "p.20" → "20", "pp.328–332" → "328–332". */
export function pageNumbers(ref: string): string {
  return ref.replace(/^pp?\./, "");
}

/** The cited pages as numbers and ranges, in the order cited: "32, 41" or "328–332". */
export function pagesList(refs: readonly string[]): string {
  return refs.map(pageNumbers).join(", ");
}

/** The form an issue opens on (the mockup's select()): confirm, else a reading, else a written answer. */
export function defaultMode(issue: SourceIssue): AnswerMode {
  if (provisionalPages(issue).length > 0) return "confirm";
  if (issue.kind === "contradiction" && issue.pages.length > 0) return "reading";
  return "text";
}

/** Whether a form can be used on this issue: a reading needs cited pages, a confirmation needs an inferred value. */
export function modeAvailable(issue: SourceIssue, mode: AnswerMode): boolean {
  if (mode === "reading") return issue.pages.length > 0;
  if (mode === "confirm") return provisionalPages(issue).length > 0;
  return true;
}

const STATUS_ORDER: Readonly<Record<SourceIssueStatus, number>> = {
  open: 0,
  answered: 1,
  waived: 2,
};

/** The API's order: open first, then answered, then waived; within a status the order it came in. */
export function inQueueOrder(issues: readonly SourceIssue[]): SourceIssue[] {
  return issues
    .map((issue, index) => ({ issue, index }))
    .sort(
      (a, b) => STATUS_ORDER[a.issue.status] - STATUS_ORDER[b.issue.status] || a.index - b.index,
    )
    .map(({ issue }) => issue);
}

/** The queue with this issue replaced by the API's answer, re-ordered, with the counts to match. */
export function withIssue(queue: SourceIssueQueue, updated: SourceIssue): SourceIssueQueue {
  const issues = inQueueOrder(queue.issues.map((i) => (i.id === updated.id ? updated : i)));
  return { issues, summary: summarizeSourceIssues(issues) };
}

export function matchesFilters(issue: SourceIssue, filters: Filters): boolean {
  if (filters.kind !== "" && issue.kind !== filters.kind) return false;
  if (filters.origin !== "" && issue.origin !== filters.origin) return false;
  if (filters.guide !== "" && issue.guide !== filters.guide) return false;
  if (filters.status === "blocking") return holdsRender(issue);
  return filters.status === "" || issue.status === filters.status;
}

/** The guides the queue has issues for, in the order they first appear. */
export function guidesIn(issues: readonly SourceIssue[]): string[] {
  return [...new Set(issues.map((i) => i.guide))];
}

/** Who may do what here. The API decides; this only decides what to draw (src/auth/guard.tsx). */
export interface Abilities {
  /** Answer and waive: a client reviewer who is not also our side (identity rules, ADR-031). */
  readonly answer: boolean;
  /** Reopen an answered or waived issue with a follow-up question: content owners. */
  readonly reopen: boolean;
}

export function abilitiesOf(roles: readonly string[]): Abilities {
  const ourSide = roles.includes("content-owner") || roles.includes("tenant-admin");
  return {
    answer: roles.includes("client-reviewer") && !ourSide,
    reopen: roles.includes("content-owner"),
  };
}

/** The packs and segment titles the production-plan catalogue gives, in the order it lists them. */
export interface Catalogue {
  readonly packs: readonly string[];
  /** A segment's title, or undefined where the catalogue does not list it. */
  readonly titleOf: (packId: string, segmentId: string) => string | undefined;
}

export function catalogueOf(
  segments: readonly { packId: string; titleId: string; title: string }[],
): Catalogue {
  const titles = new Map(segments.map((s) => [`${s.packId}/${s.titleId}`, s.title]));
  return {
    packs: [...new Set(segments.map((s) => s.packId))],
    titleOf: (packId, segmentId) => titles.get(`${packId}/${segmentId}`),
  };
}

/** Where a reviewer lands with no pack named and nothing open anywhere. */
export const FALLBACK_PACK = "ops-console-dashboard";

/** The first pack with open issues, else the fallback. `open` is each pack's open count, by pack. */
export function defaultPack(packs: readonly string[], open: ReadonlyMap<string, number>): string {
  return packs.find((p) => (open.get(p) ?? 0) > 0) ?? FALLBACK_PACK;
}
